/**
 * cn() — Tailwind class composition helper.
 *
 * Combines clsx (conditional classes) with tailwind-merge (conflict resolution)
 * so that later Tailwind utilities override earlier ones for the same property.
 * This is the canonical helper used by every shadcn/ui component.
 *
 * @example
 *   cn("px-2 py-1", isActive && "bg-primary", className)
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a USD number as a currency string with appropriate precision:
 *   - >= $1     : 2 decimals  ($12.34)
 *   - < $1      : 4 decimals  ($0.0234)
 *   - < $0.0001 : 6 decimals  ($0.000012)
 *
 * Always uses tabular-nums-friendly formatting for finance-grade tables.
 */
export function formatUsd(value: number, opts?: { compact?: boolean }): string {
  if (!Number.isFinite(value)) return '$0.00';

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (opts?.compact && abs >= 1000) {
    return `${sign}$${new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(abs)}`;
  }

  let fractionDigits: number;
  if (abs >= 1) fractionDigits = 2;
  else if (abs >= 0.0001) fractionDigits = 4;
  else fractionDigits = 6;

  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

/** Format an integer count (tokens, requests) with thousands separators. */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Math.round(value).toLocaleString('en-US');
}

/** Format a percentage delta with sign. */
export function formatPctDelta(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return '0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(fractionDigits)}%`;
}
